import { registerDestructor } from '@ember/destroyable';
import { type Registry as Services, service } from '@ember/service';
import Modifier from 'ember-modifier';
import createAccessFlags from 'lumen/utils/mount-react/create-access-flags';
import createCompatibilityRouter from 'lumen/utils/mount-react/create-compatibility-router';
import createFeatureToggleFlags from 'lumen/utils/mount-react/create-feature-toggle-flags';
import createIntl from 'lumen/utils/mount-react/create-intl';
import {
  createAccess,
  createFeatureToggles,
  mount,
  type MountableComponent,
  type MountContext,
  type MountHandle,
} from 'shared-react/lumen';

interface MountReactSignature<Props extends object> {
  Args: {
    Positional: [
      component: MountableComponent<Props>,
      ...props: [props?: Props],
    ];
  };
  Element: Element;
}

/**
 * The single seam between Ember and shared-react: mount an opaque
 * MountableComponent (unmounting it on teardown), backing shared-react's
 * access, router and intl contracts with Ember's AccessService,
 * RouterService and ember-intl. All React bootstrapping lives in
 * shared-react's self-contained build, so lumen needs no React dependency of
 * its own.
 */
export default class MountReact<Props extends object> extends Modifier<
  MountReactSignature<Props>
> {
  @service declare access: Services['access'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  #handle?: MountHandle<Props>;

  modify(
    element: Element,
    positional: MountReactSignature<Props>['Args']['Positional'],
  ): void {
    const [component, props] = positional;

    // Rebuilt every run so tracked reads land inside Ember's tracking frame.
    const accessFlags = createAccessFlags(this.access);
    const featureToggleFlags = createFeatureToggleFlags(this.featureToggle);
    const context: MountContext = {
      access: createAccess(accessFlags.features, accessFlags.hasRates),
      featureToggles: createFeatureToggles(featureToggleFlags),
      intl: createIntl(this.intl),
      router: createCompatibilityRouter(this.router),
    };

    if (!this.#handle) {
      this.#handle = mount(element, component, { ...context, props });
      registerDestructor(this, () => this.#handle?.unmount());
    } else {
      this.#handle.render(context, props);
    }
  }
}
